import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {AbortController | null} — controls the window resize listener */
  #scrollAbortController = null;

  /** @type {ResizeObserver | null} — tracks header-group size changes to reposition the submenu */
  #headerGroupObserver = null;

  /** @type {MutationObserver | null} — tracks active submenu mutations that affect measured height/shadow */
  #submenuObserver = null;

  /** @type {HTMLElement | null} — currently observed active submenu */
  #observedSubmenu = null;

  /** @type {MutationObserver | null} — tracks external style writes on <header-menu> */
  #styleObserver = null;

  /** Prevent recursive style normalization loops */
  #normalizingHierarchyClip = false;

  /** Whether the menu is locked open for debug/styling purposes */
  #debugLock = false;

  /** @type {MediaQueryList | null} — detects the tablet/laptop scroll-mode breakpoint (750–1199px) */
  #scrollModeQuery = null;

  /** @type {AbortController | null} — controls the nav-list scroll listener for the dynamic mask */
  #navScrollAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    // Ensure --submenu-height and related variables start at 0. External Shopify
    // app extensions (e.g. nav-hierarchy) may write --submenu-height to this
    // element's inline style during page initialisation. Without this reset the
    // ::after background element inherits a non-zero height, and even at opacity:0
    // it intercepts pointer events and makes page content un-clickable.
    this.#clearSubmenuPosition();

    this.#startStyleSync();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);

    // Debug mode: lock a specific mega menu open for styling
    this.#checkDebugMode();

    // Clean up accessibility attributes for empty dynamic submenus
    onDocumentLoaded(this.#cleanupEmptySubmenus);

    // Reset submenu position after the document (and any external app extensions)
    // has fully loaded, in case an extension wrote --submenu-height during init.
    onDocumentLoaded(() => {
      if (!this.#state.activeItem) {
        this.#clearSubmenuPosition();
      }
    });

    // Deferred scroll-mode init: ensures overflow-list is fully initialised first
    onDocumentLoaded(this.#initScrollMode);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#scrollAbortController?.abort();
    this.#stopSubmenuSync();
    this.#stopStyleSync();
    this.#stopScrollMode();
  }

  /**
   * Register console helpers and auto-activate from URL param for debug mode.
   *
   * Usage:
   *   URL:     ?mega-menu-debug=Dice%20%26%20Dice%20Sets  (activates and locks the matching menu item)
   *   Console: __debugMegaMenu('Dice & Dice Sets')       (reloads page with URL param — persists hot-reload)
   *            __closeDebugMegaMenu()                    (releases lock without reload)
   */
  #checkDebugMode() {
    window.__debugMegaMenu = (/** @type {string} */ title) => {
      const url = new URL(location.href);
      url.searchParams.set('mega-menu-debug', title);
      location.href = url.toString();
    };

    window.__closeDebugMegaMenu = () => {
      this.#debugLock = false;
      window.__debugMegaMenuEnforcer?.disconnect();
      this.#deactivate();
      console.info('[mega-menu] Debug lock released.');
    };

    const debugTitle = new URLSearchParams(location.search).get('mega-menu-debug');
    if (!debugTitle) return;

    // Single rAF to let the component finish its connectedCallback, then activate.
    // #activateByTitle uses a MutationObserver to wait for nav-hierarchy injection,
    // so no further fixed delays are needed here.
    requestAnimationFrame(() => {
      this.#activateByTitle(debugTitle);
    });
  }

  /**
   * Find a menu item by its visible title text and activate it with a debug lock.
   * If nav-hierarchy has not yet injected the submenu, waits for it via MutationObserver
   * rather than racing against a fixed delay.
   * @param {string} title - The menu item title to match (case-insensitive, trimmed).
   */
  #activateByTitle(title) {
    const normalised = title.trim().toLowerCase();
    const menuItems = this.querySelectorAll('[ref="menuitem"]');

    /** @type {HTMLElement | null} */
    let targetContainer = null;

    for (const item of menuItems) {
      if (item.textContent?.trim().toLowerCase() === normalised) {
        targetContainer = /** @type {HTMLElement} */ (item.parentElement);
        break;
      }
    }

    if (!targetContainer) {
      console.warn(
        `[mega-menu] No menu item found matching "${title}". Available items:`,
        Array.from(menuItems).map((el) => el.textContent?.trim())
      );
      return;
    }

    const doActivate = () => {
      // Clear any item that became active via real pointer events between page-load
      // and this deferred callback, so the activate() guard doesn't incorrectly
      // block switching to the debug target while the debug lock is held.
      this.#state.activeItem = null;
      this.#debugLock = true;
      // Call activate directly
      this.activate(targetContainer);

      // Enforce the open state: even if something deactivates the menu after we
      // activate (e.g., a late-firing pointer event during page load), immediately
      // re-activate it. This is needed because the framework can deactivate via
      // paths we cannot fully intercept (e.g., synthesised focus/blur events).
      const menuItem = /** @type {HTMLElement | null} */ (targetContainer.querySelector('[ref="menuitem"]'));
      const submenu = /** @type {HTMLElement | null} */ (targetContainer.querySelector('[ref="submenu[]"]'));

      const enforceOpen = () => {
        if (!this.#debugLock) return;
        if (menuItem && menuItem.getAttribute('aria-expanded') !== 'true') {
          this.#state.activeItem = null;
          this.activate(targetContainer);
        }
        if (submenu && !submenu.hasAttribute('data-active')) {
          submenu.dataset.active = '';
        }
      };

      const enforcer = new MutationObserver(enforceOpen);
      if (menuItem) enforcer.observe(menuItem, { attributes: true, attributeFilter: ['aria-expanded'] });
      if (submenu) enforcer.observe(submenu, { attributes: true, attributeFilter: ['data-active'] });
      window.__debugMegaMenuEnforcer = enforcer;

      console.info(`[mega-menu] Locked open: "${title}". Call __closeDebugMegaMenu() to release.`);
    };

    // If nav-hierarchy has already injected the submenu, activate immediately.
    if (targetContainer.querySelector('[ref="submenu[]"]')) {
      doActivate();
      return;
    }

    // Otherwise observe the <li> for the submenu being injected (nav-hierarchy runs after
    // header-menu and overflow-list are both defined, so it may not have run yet).
    console.info(`[mega-menu] Waiting for submenu injection for "${title}"...`);
    const observer = new MutationObserver(() => {
      if (targetContainer.querySelector('[ref="submenu[]"]')) {
        observer.disconnect();
        doActivate();
      }
    });
    observer.observe(targetContainer, { childList: true, subtree: false });

    // Safety: if nav-hierarchy never injects (e.g. shopify app dev not running), warn after 3s.
    setTimeout(() => {
      observer.disconnect();
      if (!targetContainer.querySelector('[ref="submenu[]"]')) {
        console.warn(
          `[mega-menu] Submenu for "${title}" was not injected within 3s. ` +
          `Ensure "shopify app dev" is running alongside "shopify theme dev".`
        );
      }
    }, 3000);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | Element} eventOrElement
   */
  activate = (eventOrElement) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    // Support Element arguments in addition to native events (e.g., called from #activateByTitle)
    const normalized = eventOrElement instanceof Element
      ? { target: eventOrElement }
      : eventOrElement;
    this.#debouncedActivateHandler(normalized);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {{ target: Element } | PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Suppress clip-path transitions on hierarchy submenus during rapid menu
      // switching. Without this, the incoming submenu animates from the outgoing
      // menu's height rather than appearing instantly at its own correct height.
      // The attribute is removed one rAF later so subsequent transitions are unaffected.
      this.dataset.switchingMenus = '';
      requestAnimationFrame(() => {
        delete this.dataset.switchingMenus;
      });
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Skip activation if there is no submenu or it has no content (e.g. collection
    // with no subcollections, or a top-level link like "Home" with no dropdown).
    // Must also clean up the previously active submenu and reset CSS variables,
    // otherwise the submenu background remains visible at the previous height.
    if (!submenu || isSubmenuEmpty(submenu)) {
      // Clean up the previously active submenu
      if (previouslyActiveItem) {
        const prevSubmenu = findSubmenu(previouslyActiveItem);
        if (prevSubmenu) {
          delete prevSubmenu.dataset.active;
        }
      }

      this.#stopScrollTracking();
      this.#stopSubmenuSync();
      this.#clearSubmenuPosition();
      this.dataset.overflowExpanded = 'false';

      this.#state.activeItem = null;
      this.ariaExpanded = 'false';
      item.ariaExpanded = 'false';
      return;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    // Force a reflow to ensure content-visibility: visible takes effect
    // before measuring height (content-visibility: auto skips rendering)
    const submenuInner = findSubmenuInner(submenu);
    if (submenuInner) {
      // Reading offsetHeight forces layout calculation
      void submenuInner.offsetHeight;
    }

    // Measure inner content height using scrollHeight - offsetHeight may be constrained
    // by clip-path or other CSS, but scrollHeight gives the actual content size
    const submenuHeight = submenuInner ? submenuInner.scrollHeight : (submenu ? submenu.scrollHeight : 0);

    // Calculate the actual top position for the fixed-positioned mega menu
    // Note: #header-group may have display:contents when sticky, so we measure its last child instead
    const submenuTop = getHeaderGroupBottom();

    this.#applySubmenuPosition(submenuTop, submenuHeight, submenu);
    this.#startSubmenuSync(submenu);

    // Start tracking scroll to update position during sticky header transitions
    this.#startScrollTracking();
  };

  /**
   * Apply all CSS variables for an open submenu.
   * @param {number} submenuTop - Distance from viewport top to header bottom (px)
   * @param {number} submenuHeight - Height of the submenu content (px)
   * @param {HTMLElement | null} [submenu] - The submenu element, used to measure box-shadow extent
   */
  #applySubmenuPosition(submenuTop, submenuHeight, submenu = null) {
    const shadowExtent = getSubmenuShadowBottomExtent(submenu);
    const clipBottom = submenuHeight + shadowExtent;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-top', `${submenuTop}px`);
    this.style.setProperty('--submenu-opacity', '1');
    // Required by collection-hierarchy.css: top:0 + translateY positions the element;
    // clip-path rect clips it to the visible content area. Defaults are 0 → invisible.
    this.style.setProperty('--hierarchy-submenu-y', `${submenuTop}px`);
    this.style.setProperty('--hierarchy-clip-bottom', `${clipBottom}px`);
  }

  /**
   * Reset all submenu CSS variables to the closed (hidden) state.
   */
  #clearSubmenuPosition() {
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.style.setProperty('--hierarchy-clip-bottom', '0px');
  }

  /**
   * Normalize hierarchy clip-bottom after external scripts update inline style.
   * This protects against app-extension writes that set clip-bottom to content
   * height only (excluding shadow extent).
   */
  #normalizeHierarchyClipBottom() {
    if (this.#normalizingHierarchyClip) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const submenu = findSubmenu(activeItem);
    if (!(submenu instanceof HTMLElement)) return;

    const currentHeight = parseFloat(this.style.getPropertyValue('--submenu-height') || '0');
    const currentClip = parseFloat(this.style.getPropertyValue('--hierarchy-clip-bottom') || '0');

    if (!Number.isFinite(currentHeight) || !Number.isFinite(currentClip) || currentHeight <= 0) return;

    const expectedClip = currentHeight + getSubmenuShadowBottomExtent(submenu);
    if (Math.abs(currentClip - expectedClip) < 0.5) return;

    this.#normalizingHierarchyClip = true;
    this.style.setProperty('--hierarchy-clip-bottom', `${expectedClip}px`);
    this.#normalizingHierarchyClip = false;
  }

  /**
   * Observe inline style mutations on the host and normalize hierarchy clipping.
   */
  #startStyleSync() {
    this.#stopStyleSync();

    this.#styleObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          this.#normalizeHierarchyClipBottom();
          break;
        }
      }
    });

    this.#styleObserver.observe(this, { attributes: true, attributeFilter: ['style'] });
  }

  /**
   * Disconnect inline style observer.
   */
  #stopStyleSync() {
    this.#styleObserver?.disconnect();
    this.#styleObserver = null;
  }

  /**
   * Observe the active submenu for late hierarchy injection/class updates and
   * re-apply measured position/height/shadow after DOM settles.
   * @param {HTMLElement | null} submenu
   */
  #startSubmenuSync(submenu) {
    this.#stopSubmenuSync();
    if (!(submenu instanceof HTMLElement)) return;

    this.#observedSubmenu = submenu;

    const sync = () => {
      if (this.#observedSubmenu !== submenu || !submenu.isConnected) return;

      const submenuTop = getHeaderGroupBottom();
      const submenuInner = findSubmenuInner(submenu);
      const submenuHeight = submenuInner ? submenuInner.scrollHeight : submenu.scrollHeight;
      this.#applySubmenuPosition(submenuTop, submenuHeight, submenu);
    };

    // First repaint/microtask passes often contain stale hierarchy state.
    requestAnimationFrame(sync);
    setTimeout(sync, 0);
    setTimeout(sync, 50);

    this.#submenuObserver = new MutationObserver(() => {
      requestAnimationFrame(sync);
    });

    this.#submenuObserver.observe(submenu, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-active'],
      childList: true,
      subtree: true,
    });
  }

  /**
   * Disconnect active submenu synchronization observer.
   */
  #stopSubmenuSync() {
    this.#submenuObserver?.disconnect();
    this.#submenuObserver = null;
    this.#observedSubmenu = null;
  }

  /**
   * Start tracking header size changes to keep the submenu position accurate.
   *
   * Uses ResizeObserver on the header-group element (and its children, since
   * header-group may be `display:contents` when sticky) instead of scroll events.
   * ResizeObserver fires *after* layout settles — i.e. after CSS transitions
   * complete — so `getHeaderGroupBottom()` returns the correct value when it
   * fires, unlike scroll events which fire mid-transition.
   *
   * Also registers a window resize listener to recalculate submenu height when
   * the viewport width changes (content may reflow, changing grid height).
   */
  #startScrollTracking() {
    this.#stopScrollTracking();

    const updateTop = () => {
      const submenuTop = getHeaderGroupBottom();
      this.style.setProperty('--submenu-top', `${submenuTop}px`);
      this.style.setProperty('--hierarchy-submenu-y', `${submenuTop}px`);
    };

    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      this.#headerGroupObserver = new ResizeObserver(updateTop);
      // Observe the container and each child — header-group may be display:contents,
      // in which case the observer fires on children rather than the parent.
      this.#headerGroupObserver.observe(headerGroup);
      for (const child of headerGroup.children) {
        this.#headerGroupObserver.observe(child);
      }
    }

    this.#scrollAbortController = new AbortController();
    const { signal } = this.#scrollAbortController;

    // Scroll listener: the announcement bar hides via CSS animation (opacity/translateY),
    // not a size change, so ResizeObserver alone misses the position shift. A rAF-throttled
    // scroll listener catches the position update on every scroll tick without blocking the
    // main thread.
    let scrollRafPending = false;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollRafPending) return;
        scrollRafPending = true;
        requestAnimationFrame(() => {
          scrollRafPending = false;
          updateTop();
        });
      },
      { passive: true, signal }
    );

    window.addEventListener(
      'resize',
      () => {
        // Viewport width change may reflow the subcollection grid — remeasure both
        // height and top position in one pass to avoid stale values.
        const activeItem = this.#state.activeItem;
        if (!activeItem) return;
        const activeSubmenu = findSubmenu(activeItem);
        if (!activeSubmenu) return;
        const submenuTop = getHeaderGroupBottom();
        const submenuHeight = activeSubmenu.getBoundingClientRect().height;
        this.#applySubmenuPosition(submenuTop, submenuHeight, activeSubmenu);
      },
      { passive: true, signal }
    );
  }

  /**
   * Stop tracking header size and viewport resize.
   */
  #stopScrollTracking() {
    this.#headerGroupObserver?.disconnect();
    this.#headerGroupObserver = null;
    this.#scrollAbortController?.abort();
    this.#scrollAbortController = null;
  }

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;
    if (this.#debugLock) return;

    // Stop tracking scroll when menu closes
    this.#stopScrollTracking();
    this.#clearSubmenuPosition();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const submenu = findSubmenu(item);

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Remove aria-haspopup/aria-controls from menu items whose dynamic
   * submenus rendered empty (collection with no subcollections).
   */
  #cleanupEmptySubmenus = () => {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    for (const submenu of submenus) {
      if (submenu instanceof HTMLElement && isSubmenuEmpty(submenu)) {
        const menuItem = submenu.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
        if (menuItem) {
          menuItem.removeAttribute('aria-haspopup');
          menuItem.removeAttribute('aria-controls');
          menuItem.removeAttribute('aria-expanded');
        }
      }
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  // ─── Tablet / laptop scroll mode ─────────────────────────────────────────

  /**
   * Initialise the scroll-mode media query.
   * Deferred via onDocumentLoaded so overflow-list is fully initialised first.
   */
  #initScrollMode = () => {
    this.#scrollModeQuery = window.matchMedia('(min-width: 750px) and (max-width: 1199px)');
    this.#scrollModeQuery.addEventListener('change', this.#onScrollModeChange);
    this.#onScrollModeChange();
  };

  /**
   * Handle scroll-mode media query change.
   * At 750–1199px: disable overflow reflow, show all items, enable horizontal scroll.
   * At other widths: restore normal overflow-list reflow.
   */
  #onScrollModeChange = () => {
    const overflowList = /** @type {any} */ (this.refs.overflowMenu);
    if (!overflowList) return;

    if (this.#scrollModeQuery?.matches) {
      overflowList.showAll?.();
      this.#startNavScrollMask();
    } else {
      overflowList.removeAttribute('disabled');
      this.#stopNavScrollMask();
    }
  };

  /**
   * Attach a scroll listener to the nav list shadow part and update the CSS
   * data-nav-scroll attribute for dynamic left/right fade masking.
   */
  #startNavScrollMask() {
    this.#stopNavScrollMask();

    const list = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="list"]');
    if (!(list instanceof HTMLElement)) return;

    this.#navScrollAbortController = new AbortController();
    const { signal } = this.#navScrollAbortController;

    const updateMask = () => {
      const overflowList = this.refs.overflowMenu;
      if (!(overflowList instanceof HTMLElement)) return;

      const atStart = list.scrollLeft === 0;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;

      let pos;
      if (atStart && atEnd) {
        pos = 'none';   // all items fit — no fade needed
      } else if (atEnd) {
        pos = 'end';    // scrolled to end — left fade only
      } else if (atStart) {
        pos = 'start';  // at beginning — right fade only
      } else {
        pos = 'middle'; // mid-scroll — both fades
      }

      overflowList.dataset.navScroll = pos;
    };

    list.addEventListener('scroll', updateMask, { passive: true, signal });
    updateMask(); // Set initial state immediately
  }

  /**
   * Remove the nav-list scroll listener and clean up the data attribute.
   */
  #stopNavScrollMask() {
    this.#navScrollAbortController?.abort();
    this.#navScrollAbortController = null;

    const overflowList = this.refs.overflowMenu;
    if (overflowList instanceof HTMLElement) {
      delete overflowList.dataset.navScroll;
    }
  }

  /**
   * Tear down all scroll-mode listeners.
   */
  #stopScrollMode() {
    this.#scrollModeQuery?.removeEventListener('change', this.#onScrollModeChange);
    this.#scrollModeQuery = null;
    this.#stopNavScrollMask();
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the submenu inner content element for height measurement.
 * With fixed positioning, we need to measure the inner content since the
 * outer container may have visibility: hidden which affects offsetHeight.
 * @param {Element | null | undefined} submenu
 * @returns {HTMLElement | null}
 */
function findSubmenuInner(submenu) {
  const inner = submenu?.querySelector('.menu-list__submenu-inner');
  return inner instanceof HTMLElement ? inner : null;
}

/**
 * Check if a submenu has no meaningful content.
 * @param {HTMLElement} submenu
 * @returns {boolean}
 */
function isSubmenuEmpty(submenu) {
  const inner = submenu.querySelector('.menu-list__submenu-inner');
  if (!inner) return true;

  // Hierarchy submenus (injected by nav-hierarchy.js) always have content
  if (inner.querySelector('.hierarchy-nav-grid')) return false;

  // Standard mega menus — check for list items
  const items = inner.querySelectorAll('.mega-menu__column, .mega-menu__link');
  return items.length === 0;
}

/**
 * Calculate how many pixels below the element's bottom edge a box-shadow extends.
 * Parses the computed box-shadow value and returns the maximum downward extent
 * (offset-y + blur-radius + spread-radius) across all non-inset shadow layers.
 * @param {string} boxShadow - Computed box-shadow CSS value
 * @returns {number}
 */
function getShadowBottomExtent(boxShadow) {
  if (!boxShadow || boxShadow === 'none') return 0;
  let maxExtent = 0;
  // Split multiple shadow layers on commas not inside parentheses (e.g. rgba(...))
  for (const layer of boxShadow.split(/,(?![^(]*\))/)) {
    if (/\binset\b/i.test(layer)) continue;
    // Collect all px values in order: offset-x, offset-y, blur-radius, spread-radius
    const px = [...layer.matchAll(/-?[\d.]+px/g)].map((m) => parseFloat(m[0]));
    if (px.length < 2) continue;
    const offsetY = px[1] ?? 0;
    const blur = px[2] ?? 0;
    const spread = px[3] ?? 0;
    const extent = offsetY + blur + spread;
    if (extent > maxExtent) maxExtent = extent;
  }
  return maxExtent;
}

/**
 * Get the shadow extent for submenu clipping.
 * Hierarchy submenus have a box-shadow applied, but nav-hierarchy may not have
 * finished injecting the grid content yet, so we can't reliably detect hierarchy
 * by checking for .hierarchy-nav-grid. Instead, check the computed style on the
 * submenu itself; it will have box-shadow applied via CSS rule
 * .header-menu .menu-list__submenu.hierarchy-submenu when nav-hierarchy injects.
 * @param {HTMLElement | null | undefined} submenu
 * @returns {number}
 */
function getSubmenuShadowBottomExtent(submenu) {
  if (!(submenu instanceof HTMLElement)) return 0;

  // Try measuring the submenu directly first
  const submenuBoxShadow = getComputedStyle(submenu).boxShadow;
  const extent = getShadowBottomExtent(submenuBoxShadow);

  // If we got a positive extent from the submenu, use it
  if (extent > 0) {
    return extent;
  }

  // Fallback: check if this is/will be a hierarchy submenu
  const hasHierarchyGrid = submenu.querySelector('.hierarchy-nav-grid') !== null;
  const hasHierarchyClass = submenu.classList.contains('hierarchy-submenu');

  if (hasHierarchyGrid || hasHierarchyClass) {
    return 12;
  }

  // Additional fallback: even if nav-hierarchy hasn't injected yet, this store might use
  // hierarchy menus. The theme CSS applies --default-box-shadow to all .hierarchy-submenu
  // elements. Since we can't reliably detect hierarchy before nav-hierarchy runs (timing
  // issue with class addition), we assume 12px is needed. If this is a regular menu
  // (no hierarchy), an extra 12px of clip-path is harmless and won't break anything.
  return 12;
}

/**
 * Get the bottom position of the header group for mega menu positioning.
 * Note: #header-group may have `display: contents` when sticky, which makes
 * getBoundingClientRect() return zeros. We find the lowest child element instead.
 * @returns {number}
 */
function getHeaderGroupBottom() {
  const headerGroup = document.getElementById('header-group');
  if (!headerGroup) return 0;

  // Try direct measurement first (works when not display: contents)
  const rect = headerGroup.getBoundingClientRect();
  if (rect.height > 0) {
    return rect.bottom;
  }

  // Fallback: find the bottommost child element
  let maxBottom = 0;
  for (const child of headerGroup.children) {
    const childRect = child.getBoundingClientRect();
    if (childRect.bottom > maxBottom) {
      maxBottom = childRect.bottom;
    }
  }
  return maxBottom;
}
